import Drawer from "components/BaseWidgets/drawer";
import "./style.scss";
import Button from "components/BaseWidgets/button";
import DetailsCard from "./components/DetailsCard";
import {
  useNotificationListing,
  useclearNotification,
  usereadNotification,
} from "hooks/api/useNotification";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { NotificationRouteMap } from "../../helper";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import NotificationModal from "components/CustomWidgets/notificationModal";

function Notification({ open, setOpen }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState("Happy day!");
  const [notificationMessage, setNotificationMessage] = useState("");

  const {
    data: NotificationListing,
    isLoading,
    refetch,
    isFetching,
  } = useNotificationListing({
    page,
    limit: 10,
  });

  // clear notifications
  const { mutate: clearNotification, isLoading: clearing } =
    useclearNotification({
      onSuccess: () => {
        queryClient.setQueryData(["notifications"], {
          notification_list: [],
        });
        queryClient.invalidateQueries(["notifications"]);
        setAllNotifications([]);
      },
    });

  const {
    mutate: readNotification,
    isLoading: isReding,
    isPending,
  } = usereadNotification({
    onSuccess: () => queryClient.invalidateQueries(["notifications"]),
  });

  useEffect(() => {
    if (NotificationListing?.notification_list?.length) {
      setAllNotifications((prev) =>
        page === 1
          ? NotificationListing.notification_list
          : [...prev, ...NotificationListing.notification_list]
      );
    }
  }, [NotificationListing]);

  const pagination = NotificationListing?.pagination_details;
  const hasMore = pagination && page * pagination.limit < pagination.count;

  const handleNotificationClick = (item) => {
    if (item.read_status === "Unread") {
      readNotification({ notification_id: item.id });
    }

    const typeId = item.notification_type_id;
    const route = NotificationRouteMap[typeId];

    if (!route && !(typeId >= 18 && typeId <= 25) && typeId !== 31) {
      return;
    }

    switch (typeId) {
      case 1:
        if (item.data?.aec_id) navigate(`${route}/${item.data.aec_id}`);
        else navigate("/explore");
        break;
      case 7:
        navigate(route, { state: { tabId: "2", activeTabId: "1" } });
        break;
      case 10:
        navigate(route, { state: { tabId: "2", activeTabId: "2" } });
        break;
      case 11:
        navigate(route, { state: { tabId: "2", activeTabId: "1" } });
        break;
      case 12:
        navigate(route, { state: { tabId: "4", activeTabId: "1" } });
        break;
      case 13:
        navigate(route, { state: { tabId: "4", activeTabId: "2" } });
        break;
      case 14:
        navigate(route, { state: { tabId: "5", activeTabId: "1" } });
        break;
      case 15:
        navigate(route, { state: { tabId: "5", activeTabId: "1" } });
        break;
      case 26:
        navigate(route, { state: { tabId: "1", activeTabId: "2" } });
        break;
      case 27:
        navigate(route, { state: { tabId: "1", activeTabId: "2" } });
        break;
      case 31:
        setOpen(false);
        return;

      default:
        if (
          item.notification_type_id >= 18 &&
          item.notification_type_id <= 25
        ) {
          const titles = {
            18: "Happy Diwali!",
            19: "Merry Christmas!",
            20: "Happy Holi!",
            21: "Happy Dussehra!",
            22: "Happy Doctor’s Day! ",
            23: "Happy Teacher’s Day!",
            24: "Happy Men’s Day!",
            25: "Happy Women’s Day!",
          };
          setModalOpen(true);
          setNotificationTitle(
            titles[item.notification_type_id] || "Happy Day!"
          );
          setNotificationMessage(item?.message || "");
        } else {
          navigate(route);
        }
    }

    setOpen(false);
  };

  // Group by date
  const today = dayjs().format("YYYY-MM-DD");
  const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

  const grouped = allNotifications.reduce(
    (acc, item) => {
      const date = dayjs(item.created_at).format("YYYY-MM-DD");
      if (date === today) acc.today.push(item);
      else if (date === yesterday) acc.yesterday.push(item);
      else {
        if (!acc.others[date]) acc.others[date] = [];
        acc.others[date].push(item);
      }
      return acc;
    },
    { today: [], yesterday: [], others: {} }
  );

  const title = (
    <div className="flex items-center gap-4">
      <h1 className="font-title-normal leading-1">Notifications</h1>
      {allNotifications.length > 0 && (
        <Button
          variant="link"
          onClick={() => clearNotification({})}
          disabled={clearing}
        >
          {clearing ? "Clearing..." : "Clear all"}
        </Button>
      )}
    </div>
  );

  return (
    <Drawer title={title} isOpen={open} onClose={() => setOpen(false)}>
      <div className="flex flex-col gap-5 notification-wrapper">
        {isLoading && page === 1 ? (
          <div className="p-4">Loading...</div>
        ) : allNotifications.length === 0 ? (
          <div className="text-center text-gray-500 py-6">
            No notifications found
          </div>
        ) : (
          <>
            {[
              { label: "Today", data: grouped.today },
              { label: "Yesterday", data: grouped.yesterday },
              ...Object.entries(grouped.others).map(([date, items]) => ({
                label: dayjs(date).format("DD MMM YYYY"),
                data: items,
              })),
            ]
              .filter((g) => g.data.length > 0)
              .map(({ label, data }) => (
                <div className="flex flex-col gap-1" key={label}>
                  <span className="week-name">{label}</span>
                  <div className="flex flex-col">
                    {data.map((item) => (
                      <DetailsCard
                        isDisabled={isPending || isReding}
                        key={item.id}
                        data={{
                          id: item?.id,
                          section: label,
                          time: dayjs(item?.created_at).format("hh:mm A"),
                          title: item?.title,
                          details:item,
                          aec_id:item?.data?.aec_id,
                          subtitle: item?.message,
                          avatar: item?.profile_image,
                          unread: item?.read_status === "Unread",
                          type:
                            typeof item?.notification_type === "string"
                              ? item?.notification_type.toLowerCase()
                              : "",
                        }}
                        onClick={() => handleNotificationClick(item)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            {hasMore && (
              <div className="text-center my-4">
                <Button
                  variant="primary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={isFetching}
                >
                  {isFetching ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <NotificationModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={notificationTitle}
        message={notificationMessage}
      />
    </Drawer>
  );
}

export default Notification;
